import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SoftTypography from 'components/SoftTypography';
import SoftBox from 'components/SoftBox';
import { Box } from '@mui/material';
import { LoaderContext } from 'shared/context/loaderContext';
import styles from './invoice-list.module.scss';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorLoggingService from 'shared/services/errorlogging.service';
import useInvoicingService from './useInvoicingService';
import { UserDataContext } from 'shared/context/userDataContext';
import SearchField from 'shared/components/SearchField/SearchField';

function InvoiceList() {
  const { userRoles, userData } = useContext(UserDataContext);
  const { setLoading } = useContext(LoaderContext);
  const { t } = useTranslation();
  const errorLoggingService = ErrorLoggingService();
  const { getInvoices, getInvoicingAgencies } = useInvoicingService();
  const [invoices, setInvoices] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [filteredAgencies, setFilteredAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchInvoices = async (agency) => {
    try {
      setLoading(true);

      const fetchedInvoices = await getInvoices(agency);

      setInvoices(fetchedInvoices);
    } catch (err) {
      errorLoggingService.log('Error fetching invoices:', err);

      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const fetchedAgencies = await getInvoicingAgencies();
      setAgencies(fetchedAgencies);
    } catch (err) {
      errorLoggingService.log('Error fetching agencies:', err);

      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAgencySelect = (agency) => {
    setSelectedAgency(agency);
    fetchInvoices(agency);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredAgencies(agencies);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const filtered = agencies.filter((agency) =>
      agency.toLowerCase().includes(lowerTerm),
    );

    setFilteredAgencies(filtered);
  };

  useEffect(() => {
    if (userRoles.invoicingAll) {
      fetchAgencies();
    } else if (userRoles.invoicing) {
      fetchInvoices(userData?.ssoData?.businessCode);
    }
  }, []);

  useEffect(() => {
    setFilteredAgencies(agencies);
  }, [agencies]);

  if (error)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box className={styles.topContainer}>
          <SoftBox sx={{ marginTop: '6rem' }}>
            <SoftTypography
              variant="h2"
              fontWeight="bold"
              sx={{ marginBottom: '2rem' }}
            >
              {t('INVOICES.invoiceList')}
            </SoftTypography>
          </SoftBox>
          <p>
            {t('INVOICES.errorLoading')} {error?.message || ''}
          </p>
        </Box>
      </DashboardLayout>
    );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box className={styles.topContainer}>
        <SoftBox sx={{ marginTop: '6rem' }}>
          <SoftTypography
            variant="h2"
            fontWeight="bold"
            sx={{ marginBottom: '2rem' }}
          >
            {t('INVOICES.invoiceList')}
          </SoftTypography>
        </SoftBox>
      </Box>

      <Box className={styles.bodyContainer}>
        {userRoles.invoicingAll && (
          <Box className={styles.listWrapper}>
            <SoftTypography variant="h4" className={styles.listTitle}>
              {t('INVOICES.agencies')}
            </SoftTypography>
            <SearchField
              searchFunc={handleSearch}
              searchCriteria={searchTerm}
              onSearchCriteriaChange={(event) =>
                setSearchTerm(event.target.value)
              }
              inputLabel={t('INVOICES.searchAgenciesLabel')}
              inputPlaceholder={t('INVOICES.searchAgenciesPlaceholder')}
              buttonText={t('INVOICES.searchAgenciesButton')}
              containerStyles={styles.searchContainer}
              inputStyles={styles.searchInput}
            />
            {filteredAgencies.length > 0 ? (
              <Box className={styles.listContainer}>
                {filteredAgencies.map((agency) => (
                  <div
                    key={agency}
                    className={`${styles.listItem} ${
                      selectedAgency === agency && styles.selected
                    }`}
                    onClick={() => handleAgencySelect(agency)}
                  >
                    <SoftTypography variant="body1" className={styles.title}>
                      {agency}
                    </SoftTypography>
                  </div>
                ))}
              </Box>
            ) : (
              <SoftTypography variant="body1" className={styles.invoiceName}>
                {t('INVOICES.noAgencies')}
              </SoftTypography>
            )}
          </Box>
        )}

        <Box className={styles.listWrapper} style={{ flexShrink: 2 }}>
          <SoftTypography variant="h4" className={styles.listTitle}>
            {t('INVOICES.invoices')}
          </SoftTypography>
          <Box className={styles.searchContainer}></Box>
          {invoices.length > 0 ? (
            <Box className={styles.listContainer}>
              {invoices
                .sort(
                  (a, b) => new Date(b.lastModified) - new Date(a.lastModified),
                )
                .map((invoice) => (
                  <a
                    key={invoice.fileName}
                    className={styles.listItem}
                    href={invoice.url}
                    download={invoice.fileName}
                  >
                    <DescriptionIcon size="10px" color="black" />
                    <SoftTypography variant="body1" className={styles.title}>
                      {invoice.fileName}
                    </SoftTypography>
                    <SoftTypography
                      variant="caption"
                      className={styles.subtitle}
                    >
                      {new Date(invoice.lastModified).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </SoftTypography>
                  </a>
                ))}
            </Box>
          ) : userRoles.invoicingAll && !selectedAgency ? (
            <SoftTypography variant="body1" className={styles.invoiceName}>
              {t('INVOICES.noSelectedAgency')}
            </SoftTypography>
          ) : (
            <SoftTypography variant="body1" className={styles.invoiceName}>
              {t('INVOICES.noInvoices')}
            </SoftTypography>
          )}
        </Box>
      </Box>
    </DashboardLayout>
  );
}

export default InvoiceList;
